import type { HTMLAttributes, PropsWithChildren, Ref } from "react";

import { cn } from "@/ui/lib";
import { DefinedDefaults } from "@/ui/lib/types";
import { TSeparatorProps } from "@/ui/components/primitives/Separator";

/*-------------------------------------
|  Constants
-------------------------------------*/

export const ITEM_GROUP_CLASS = "flex w-full flex-col";

export const ITEM_CLASS = cn(
  "group/item relative flex w-full flex-wrap items-start gap-4 p-4",
  "rounded-sys-xs text-left outline-none transition-colors",
  "[&:is(a,button)]:cursor-pointer [&:is(a,button)]:hover:bg-surface-subtle",
  "focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-focus-primary",
);

export const ITEM_VARIANT_CLASS_MAP = {
  default: "",
  outline: "border border-solid border-muted",
  eyebrow: "",
} as const;

export const ITEM_MEDIA_CLASS = "flex shrink-0 items-center justify-center text-foreground";

export const ITEM_MEDIA_VARIANT_CLASS_MAP = {
  default: "[&_svg]:size-6",
  icon: "size-8 rounded-sys-full bg-surface-subtle [&_svg]:size-6",
  image: "size-10 overflow-hidden rounded-sys-sm [&_img]:size-full [&_img]:object-cover",
} as const;

export const ITEM_CONTENT_CLASS =
  "flex min-w-0 flex-1 flex-col justify-center gap-1 self-stretch";

export const ITEM_TITLE_CLASS = cn(
  "font-sys-display font-bold tracking-sys-tight leading-sys-1 text-foreground",
  "text-sys-xl group-data-[size=sm]/item:text-sys-lg",
  "group-data-[variant=eyebrow]/item:order-2",
);

export const ITEM_DESCRIPTION_CLASS = cn(
  "font-sys-default tracking-sys-tight text-sys-base leading-sys-1-3",
  "text-foreground-muted-strong",
  "group-data-[variant=eyebrow]/item:order-1",
);

export const ITEM_ACTIONS_CLASS =
  "flex shrink-0 items-center justify-end gap-2 self-stretch";

export const ITEM_HEADER_CLASS = "flex basis-full items-center justify-between gap-2";

export const ITEM_FOOTER_CLASS = "flex basis-full items-center justify-between gap-2";

/*-------------------------------------
|  Types
-------------------------------------*/

export type TItemGroupProps = HTMLAttributes<HTMLDivElement>;

export type TItemVariant = keyof typeof ITEM_VARIANT_CLASS_MAP;
export type TItemSize = "default" | "sm";

export type TItemProps = HTMLAttributes<HTMLDivElement> & {
  variant?: TItemVariant;
  size?: TItemSize;
  /**
   * Render the Item as its single child element (e.g. an `<a>` or `<button>`)
   * instead of a `<div>`, merging Item's styles/attributes onto it. Use when the
   * whole row should be a link or button so it keeps native semantics, focus and
   * keyboard behaviour.
   */
  asChild?: boolean;
};

export type TItemChildProps = PropsWithChildren & {
  className?: string;
  "data-component"?: string;
  ref?: Ref<HTMLElement>;
};

export type TItemMediaVariant = keyof typeof ITEM_MEDIA_VARIANT_CLASS_MAP;

export type TItemMediaProps = HTMLAttributes<HTMLDivElement> & {
  variant?: TItemMediaVariant;
};

export type TItemContentProps = HTMLAttributes<HTMLDivElement>;
export type TItemTitleProps = HTMLAttributes<HTMLParagraphElement>;
export type TItemDescriptionProps = HTMLAttributes<HTMLParagraphElement>;
export type TItemActionsProps = HTMLAttributes<HTMLDivElement>;
export type TItemHeaderProps = HTMLAttributes<HTMLDivElement>;
export type TItemFooterProps = HTMLAttributes<HTMLDivElement>;
export type TItemSeparatorProps = TSeparatorProps;

type TItemDefaultProps = DefinedDefaults<TItemProps, "variant" | "size" | "asChild">;
type TItemMediaDefaultProps = DefinedDefaults<TItemMediaProps, "variant">;

/*-------------------------------------
|  Defaults
-------------------------------------*/

export const ITEM_DEFAULTS = {
  variant: "default",
  size: "default",
  asChild: false,
} satisfies TItemDefaultProps;

export const ITEM_MEDIA_DEFAULTS = {
  variant: "default",
} satisfies TItemMediaDefaultProps;
